export interface MemberNameRecord {
  id: string
  displayName: string
  fullName?: string | null
}

export interface RankedMember<T extends MemberNameRecord = MemberNameRecord> {
  member: T
  confidence: number
  matched: boolean
}

function levenshteinDistance(a: string, b: string): number {
  const left = a.toLocaleLowerCase()
  const right = b.toLocaleLowerCase()
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)

  for (let i = 1; i <= left.length; i++) {
    const current = [i]
    for (let j = 1; j <= right.length; j++) {
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1),
      )
    }
    previous.splice(0, previous.length, ...current)
  }
  return previous[right.length]
}

/** Legacy whole-string matcher used by bank reconciliation. */
export function fuzzyMatch(
  query: string,
  target: string,
  threshold = 0.7,
): { matched: boolean; confidence: number } {
  if (!query || !target) return { matched: false, confidence: 0 }

  const normalizedQuery = query.toLocaleLowerCase().trim()
  const normalizedTarget = target.toLocaleLowerCase().trim()
  if (normalizedQuery === normalizedTarget) return { matched: true, confidence: 1 }
  if (
    normalizedQuery.includes(normalizedTarget) ||
    normalizedTarget.includes(normalizedQuery)
  ) {
    return { matched: true, confidence: 0.95 }
  }
  if (
    normalizedQuery.length >= 3 &&
    normalizedTarget.length >= 3 &&
    normalizedQuery.slice(0, 3) === normalizedTarget.slice(0, 3)
  ) {
    return { matched: true, confidence: 0.85 }
  }

  const maxLength = Math.max(normalizedQuery.length, normalizedTarget.length)
  const confidence =
    maxLength === 0
      ? 0
      : 1 - levenshteinDistance(normalizedQuery, normalizedTarget) / maxLength
  return { matched: confidence >= threshold, confidence }
}

export function extractNameParts(name: string): string[] {
  return name
    .trim()
    .split(/[,\s]+/)
    .map((part) => part.toLocaleLowerCase())
    .filter(Boolean)
}

function partScore(query: string, target: string): number {
  if (!query || !target) return 0
  if (query === target) return 1

  if (query.length >= 3 && target.length >= 3) {
    if (query.includes(target) || target.includes(query)) return 0.95
    if (query.slice(0, 3) === target.slice(0, 3)) return 0.85
  }

  const maxLength = Math.max(query.length, target.length)
  return maxLength === 0 ? 0 : 1 - levenshteinDistance(query, target) / maxLength
}

export function scoreMemberName(
  sourceName: string,
  member: MemberNameRecord,
  threshold: number,
): { matched: boolean; confidence: number } {
  const sourceParts = extractNameParts(sourceName)
  if (sourceParts.length === 0) return { matched: false, confidence: 0 }

  const candidateParts = [
    ...new Set([
      ...extractNameParts(member.fullName ?? ''),
      ...extractNameParts(member.displayName),
    ]),
  ]
  const scores = sourceParts.map((sourcePart) =>
    candidateParts.reduce(
      (best, candidatePart) => Math.max(best, partScore(sourcePart, candidatePart)),
      0,
    ),
  )
  const matchedScores = scores.filter((score) => score >= threshold)
  const matchPercentage = matchedScores.length / sourceParts.length
  const averageConfidence =
    matchedScores.length === 0
      ? 0
      : matchedScores.reduce((sum, score) => sum + score, 0) / matchedScores.length
  const confidence = Math.min(matchPercentage * averageConfidence, 1)

  return {
    matched:
      matchedScores.length > 0 &&
      matchPercentage >= 0.5 &&
      confidence >= threshold,
    confidence,
  }
}

/**
 * Scores how completely a member's known name is represented in a noisier
 * source. Useful for bank fields that may include titles, initials or account
 * metadata around the actual payer name.
 */
export function scoreMemberNameCoverage(
  sourceName: string,
  member: MemberNameRecord,
  threshold: number,
): { matched: boolean; confidence: number } {
  const sourceParts = extractNameParts(sourceName)
  const candidateParts = [
    ...new Set([
      ...extractNameParts(member.fullName ?? ''),
      ...extractNameParts(member.displayName),
    ]),
  ]
  if (sourceParts.length === 0 || candidateParts.length === 0) {
    return { matched: false, confidence: 0 }
  }

  const scores = candidateParts.map((candidatePart) =>
    sourceParts.reduce(
      (best, sourcePart) => Math.max(best, partScore(sourcePart, candidatePart)),
      0,
    ),
  )
  const matchedScores = scores.filter((score) => score >= threshold)
  const matchPercentage = matchedScores.length / candidateParts.length
  const averageConfidence =
    matchedScores.length === 0
      ? 0
      : matchedScores.reduce((sum, score) => sum + score, 0) / matchedScores.length
  const confidence = Math.min(matchPercentage * averageConfidence, 1)

  return {
    matched:
      matchedScores.length > 0 &&
      matchPercentage >= 0.5 &&
      confidence >= threshold,
    confidence,
  }
}

export function rankMembers<T extends MemberNameRecord>(
  sourceNames: readonly string[],
  members: readonly T[],
  threshold: number,
): RankedMember<T>[] {
  return members
    .map((member) => {
      const scores = sourceNames
        .filter((source) => source.trim().length > 0)
        .map((source) => scoreMemberName(source, member, threshold))
      const best = scores.reduce(
        (current, candidate) =>
          candidate.confidence > current.confidence ? candidate : current,
        { matched: false, confidence: 0 },
      )
      return { member, ...best }
    })
    .sort(
      (a, b) =>
        b.confidence - a.confidence ||
        a.member.displayName.localeCompare(b.member.displayName) ||
        a.member.id.localeCompare(b.member.id),
    )
}

export function rankMembersByNameCoverage<T extends MemberNameRecord>(
  sourceName: string,
  members: readonly T[],
  threshold: number,
): RankedMember<T>[] {
  return members
    .map((member) => ({
      member,
      ...scoreMemberNameCoverage(sourceName, member, threshold),
    }))
    .sort(
      (a, b) =>
        b.confidence - a.confidence ||
        a.member.displayName.localeCompare(b.member.displayName) ||
        a.member.id.localeCompare(b.member.id),
    )
}

/**
 * Legacy bank-import strategy: evaluate every supplied field independently
 * against every member, then retain that member's strongest successful score.
 */
export function rankMembersAcrossBankFields<T extends MemberNameRecord>(
  sourceNames: readonly string[],
  members: readonly T[],
  threshold: number,
): RankedMember<T>[] {
  const fields = sourceNames.filter((source) => source.trim().length > 0)
  return members
    .map((member) => {
      let confidence = 0
      let hasMatch = false

      for (const field of fields) {
        const fullNameMatch = member.fullName
          ? fuzzyMatch(field, member.fullName, threshold)
          : { matched: false, confidence: 0 }
        const displayNameMatch = fuzzyMatch(field, member.displayName, threshold)
        const partMatch = scoreMemberName(field, member, 0.6)
        // Preserve the noisy-field improvement while retaining the legacy
        // whole-field and input-part strategies.
        const coverageMatch = scoreMemberNameCoverage(field, member, 0.6)
        const fieldConfidence = Math.max(
          fullNameMatch.confidence,
          displayNameMatch.confidence,
          partMatch.confidence,
          coverageMatch.confidence,
        )
        const fieldMatched =
          fullNameMatch.matched ||
          displayNameMatch.matched ||
          partMatch.matched ||
          coverageMatch.matched

        if (fieldMatched && fieldConfidence > confidence) {
          confidence = fieldConfidence
          hasMatch = true
        }
      }

      return {
        member,
        confidence,
        matched: hasMatch && confidence >= threshold,
      }
    })
    .sort(
      (a, b) =>
        b.confidence - a.confidence ||
        a.member.displayName.localeCompare(b.member.displayName) ||
        a.member.id.localeCompare(b.member.id),
    )
}
